import { BackUseIcon, ShareUseIcon } from "@/containers/layout/NavigationIcons";
import { answerQuestionsWithNotOverWrite, useMasterAccount, useQuestions } from "@/hooks/useRequest/useSurveyRequest";
import { AnswerQuestionModel, SurveyType } from "@/interfaces/surveyTypes";
import { useTranslation } from "next-i18next";

import { useState } from "react";
import useOSS from "@/hooks/useRequest/useOSS";

import Button from "@/components/Button";

import { CloseXIcon } from "@/components/RFIcon";
import compressorFile from "@/utils/compressorFile";
import readAsDataUrl from "@/utils/readAsDataUrl";
import Image from "next/image";
import { getCDNWithWebp, ossFullUrl } from "@/utils/getFullWebCdnUrl";
import blurDataURL from "@/utils/blurDataURL";
import CoverContainer from "@/containers/layout/CoverContainer";
import Navigation from "@/containers/layout/Navigation";
import Observer from "@/containers/common/observer";
import clsx from "clsx";
import useInApp from "@/hooks/useIsApp";
import useEvokeApp from "@/utils/evokeApp";
import clientDeepLinks from "@/utils/clientDeepLinks";
import { MasterAccountOpenStatus } from "@/interfaces/enums";
import Toast from "@/components/Toast";
import SafeArea from "@/components/safe-area";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import useIsZH from "@/hooks/useIsZH";

const ReactPlayer = dynamic(() => import("react-player"), { ssr: false });

interface FileState {
	key: number;
	path: string | ArrayBuffer | null;
	file?: File;
}
interface Props {
	onShare: () => void;
	onSuccess: () => void;
	locale: string;
}

interface ProdCardProps {
	title: string;
	desc: string;
	imgLight: string;
	imgDark: string;
}

const FileMaxLength = 3;

export default function IdeasForm(props: Props) {
	const { locale } = useRouter();
	const { t } = useTranslation("survey-register");
	const [loading, setLoading] = useState(false);
	const [showNavigationBarBg, setShowNavigationBarBg] = useState(false);
	const isInApp = useInApp();
	const { evokeApp } = useEvokeApp();

	const { upload } = useOSS("poster");
	const [files, setFiles] = useState<FileState[]>([]);

	const isZh = useIsZH();

	const mp4 = ["zh-Hant", "zh-Hans"].includes(locale || "")
		? "https://web-cdn.rockflow.tech/h5/survey/register/Rockflow_zh_en.mp4"
		: "https://web-cdn.rockflow.tech/h5/survey/register/Rockflow_en.mp4";

	const { questionFieldToIdMap } = useQuestions({
		type: SurveyType.ai_registration,
	});
	const { masterAccount, isLoading } = useMasterAccount();

	const readFile = async (_file: File) => {
		if (!_file) return;
		const compressFile = await compressorFile(_file);
		const dataUrl = await readAsDataUrl(compressFile);
		const _files = [...files];
		_files.push({
			key: _files.length - 1,
			file: compressFile,
			path: dataUrl,
		});
		setFiles([..._files]);
	};
	const handleRemove = (key: number) => {
		const _files = [...files].filter((item) => item.key !== key);
		setFiles([..._files]);
	};

	const onSubmit = async () => {
		if (!isInApp) {
			handleEvokeApp();
			return;
		}

		// 未开户
		if (Number(masterAccount?.masterAccountOpenStatus) < MasterAccountOpenStatus.APPROVED) {
			Toast(t("SURVEY_REG_account_open_toast"));
			return;
		}

		// 未上传
		if (files.length === 0) {
			Toast(t("SURVEY_REG_ERROR_TOAST"));
			return;
		}

		if (loading) return;
		setLoading(true);

		const _files = files.filter((item) => item.file != undefined).map((item) => item.file as File);
		let images: string[] = [];

		if (_files && _files.length > 0) {
			images = await Promise.all(_files.map((_file) => upload(_file)));
		}

		try {
			let payload: AnswerQuestionModel[] = [
				{
					answer: images.join("#$#"),
					questionId: questionFieldToIdMap.get("poster"),
				},
			];

			await answerQuestionsWithNotOverWrite({
				questionType: SurveyType.ai_registration,
				payload: payload,
			});

			props.onSuccess();
		} catch (error) {
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const handleFileClick = () => {
		if (isInApp) return;
		handleEvokeApp();
	};

	const handleEvokeApp = () => {
		const url = `${window.location.origin}/${props.locale}/survey/registration?hidden_navigation_bar=1&status_bar=1`;
		const _deepLink = clientDeepLinks.webviewOutApp(url);
		evokeApp(_deepLink);
	};

	const advS: ProdCardProps[] = [
		{
			title: t("SURVEY_REG_SMART_ORDER"),
			desc: t("SURVEY_REG_SMART_ORDER_EXAMPLES"),
			imgLight: ossFullUrl("/survey/register/adv-01-light.png"),
			imgDark: ossFullUrl("/survey/register/adv-01-dark.png"),
		},
		{
			title: t("SURVEY_REG_Market_Surveillance"),
			desc: t("SURVEY_REG_Market_Surveillance_EXAMPLES"),
			imgLight: ossFullUrl("/survey/register/adv-02-light.png"),
			imgDark: ossFullUrl("/survey/register/adv-02-dark.png"),
		},
		{
			title: t("SURVEY_REG_PERSONALIZED_MEMORY"),
			desc: t("SURVEY_REG_PERSONALIZED_MEMORY_EXAMPLES"),
			imgLight: ossFullUrl("/survey/register/adv-03-light.png"),
			imgDark: ossFullUrl("/survey/register/adv-03-dark.png"),
		},
		{
			title: t("SURVEY_REG_SMART_STOCK_SELECTION"),
			desc: t("SURVEY_REG_SMART_STOCK_SELECTION_EXAMPLES"),
			imgLight: ossFullUrl("/survey/register/adv-04-light.png"),
			imgDark: ossFullUrl("/survey/register/adv-04-dark.png"),
		},
		{
			title: t("SURVEY_REG_OPTIONS_STRATEGY"),
			desc: t("SURVEY_REG_OPTIONS_STRATEGY_EXAMPLES"),
			imgLight: ossFullUrl("/survey/register/adv-05-light.png"),
			imgDark: ossFullUrl("/survey/register/adv-05-dark.png"),
		},
		{
			title: t("SURVEY_REG_SMART_INVESTMENT_EDUCATION_SUPPORT"),
			desc: t("SURVEY_REG_SMART_INVESTMENT_EDUCATION_SUPPORT_EXAMPLES"),
			imgLight: ossFullUrl("/survey/register/adv-06-light.png"),
			imgDark: ossFullUrl("/survey/register/adv-06-dark.png"),
		},
	];

	return (
		<CoverContainer>
			<div className="navigation--wrapper relative z-10 w-full max-w-screen-sm mx-auto flex flex-col">
				{isInApp && (
					<div
						className={clsx(
							"fixed left-0 top-0 z-50 w-full",
							showNavigationBarBg ? "bg-dark shadow-xl" : "bg-transparent ",
						)}
					>
						<Navigation
							title={t("SURVEY_REG_BOBBY_BETA_TEST_REGISTRATION")}
							leading={[<BackUseIcon key="back" backType="close" />]}
							trailing={[<ShareUseIcon key="share" onClick={props.onShare} />]}
						/>
					</div>
				)}

				<div className="flex-1 relative overflow-y-auto hidden-scroll-bar pb-6">
					<div className={clsx("absolute left-0", isInApp ? "top-0" : "-top-[12vh]")}>
						<Image
							width={375 * 2}
							height={722 * 2}
							priority={true}
							src={ossFullUrl("/survey/register/hero-bg.png")}
							alt="registration"
						/>
					</div>

					<div className="relative z-10">
						<div className="pt-[52vh]"></div>
						<div className="rf-page-ani rf-page-ani-delay-0 ">
							<Observer
								onVisible={(val) => {
									setShowNavigationBarBg(!val);
								}}
							/>
							<h2 className={clsx("font-semibold  w-full whitespace-pre-wrap px-4.5", isZh ? "text-2xl" : "text-lg")}>
								{t("SURVEY_REG_BOBBY_REDEFINES_AI_INVESTMENT_ASSISTANT")}
							</h2>

							<div className="mt-6 aspect-video">
								<ReactPlayer
									url={mp4}
									width="100%"
									height="100%"
									controls={true}
									preload="metaData"
									light={getCDNWithWebp("/survey/register/poster.png")}
									config={{
										file: {
											attributes: {
												controlsList: "nodownload noremoteplayback", // 禁用下载按钮
												rel: 0, // 禁用相关视频推荐
												modestbranding: 1, // 隐藏 YouTube logo
											},
										},
									}}
								/>
							</div>

							<div className="text-xs font-semibold leading-6 whitespace-pre-wrap mt-6 px-4.5">
								<div dangerouslySetInnerHTML={{ __html: t("SURVEY_REG_MORE_THAN_JUST_MECHANICAL_CHAT_BOX") }} />
								<div
									className="mt-3"
									dangerouslySetInnerHTML={{ __html: t("SURVEY_REG_NOT_JUST_CONVEYOR_OF_ENDLESS_INFO") }}
								/>
							</div>
						</div>

						<Image
							src={ossFullUrl("/survey/register/ai-bot.png")}
							priority={true}
							alt="hero"
							width={375 * 2}
							height={383 * 2}
						/>

						<div className="absolute left-0 bottom-[5.125rem]">
							<Image width={143} height={399} src={ossFullUrl("/survey/register/content-bg.png")} alt="registration" />
						</div>

						{/* 产品 */}
						<div className="relative">
							<div className="rf-page-ani rf-page-ani-delay-2 -mt-9 px-4.5 relative">
								{advS.map((item, index) => (
									<ProdCard key={index} {...item} />
								))}

								<div className="text-xs font-semibold leading-6 mt-8">
									<p className="text-[#13FFFF]">{t("Share-any-relevant-content")}</p>
									<p className="mt-3 ">{t("SURVEY_REG_PREVENT_SERVER_OVERLOAD")}</p>
								</div>
							</div>
							{/* 问卷开始 */}

							<div className="rf-page-ani rf-page-ani-delay-3 px-4.5">
								<div className="h-10" />
								<LabelField txt={t("SURVEY_REG_UPLOAD_SCREENSHOT")} />
								<div className="grid grid-cols-3 gap-5">
									{files.map((item) => (
										<GridView key={item.key}>
											<div className="p-0.5 w-full h-full">
												<div className="w-full h-full p-0.5">
													<Img src={item.path || ""} />
												</div>
											</div>

											<span className="rounded-full dark:bg-black absolute -top-1 -right-1 bg-white w-4 h-4">
												<CloseXIcon className="w-4 h-4 link-text" onClick={() => handleRemove(item.key)} />
											</span>
										</GridView>
									))}

									{files.length < FileMaxLength && (
										<GridView>
											<div className="flex h-full w-full items-center justify-center" onClick={handleFileClick}>
												{isInApp && (
													<input
														type="file"
														accept="image/*"
														onChange={(e) => {
															const _file = e.target.files?.[0] as File;
															if (!_file) return;
															readFile(_file);
															e.target.value = "";
														}}
														className="absolute left-0 top-0 opacity-0 w-full h-full"
													/>
												)}
											</div>
										</GridView>
									)}
								</div>

								<ul className="text-xxs1 font-semibold leading-[1.125rem] list-none mt-5 inter-text ">
									<li>{t("SURVEY_REG_UPLOAD_SCREENSHOT_INSTRUCTION")}</li>
									<li>{t("SURVEY_REG_POST_REQUIREMENT")}</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
				<div className="py-6 px-4.5 flex-shrink-0 bg-[#020205]">
					<Button forceDark={true} type="primary" block onClick={onSubmit} loading={loading}>
						{t("SURVEY_REG_SIGN_UP")}
					</Button>
					<SafeArea position="bottom" />
				</div>
			</div>
		</CoverContainer>
	);
}

function LabelField({ txt }: { txt: string }) {
	return <p className={`font-semibold text-sm mb-5`}>{txt}</p>;
}
function Img({ src }: { src?: string | ArrayBuffer }) {
	if (!src) return null;
	return <img src={src as any} alt="file" className="block w-full h-full object-contain" />;
}

function GridView({ children }: { children: React.ReactNode }) {
	return (
		<div
			className="aspect-square relative 
        bg-no-repeat bg-center bg-cover 
        bg-[url('https://web-cdn.rockflow.tech/h5/deposit/credentials-light.png')]
        dark:bg-[url('https://web-cdn.rockflow.tech/h5/deposit/credentials-dark.png')]"
		>
			{children}
		</div>
	);
}

function ProdCard(props: ProdCardProps) {
	return (
		<div className="mb-7 flex items-center">
			<div className="flex-shrink-0">
				<Image
					className="inline-block dark:hidden"
					src={props.imgLight}
					alt="adv"
					width={54}
					height={54}
					loading="lazy"
					placeholder="blur"
					blurDataURL={blurDataURL}
				/>
				<Image
					className="hidden dark:inline-block"
					src={props.imgDark}
					alt="adv"
					width={54}
					height={54}
					loading="lazy"
					placeholder="blur"
					blurDataURL={blurDataURL}
				/>
			</div>

			<div className="pl-4 leading-[1.5]">
				<div className="text-xxs2 font-semibold">{props.title}</div>
				<div className="mt-1.5 text-xs font-semibold leading-[1.5]">{props.desc}</div>
			</div>
		</div>
	);
}
